// servizi/accessoWeb.ts — «Continua con Google» e «Continua con Apple» sul sito web.
// Google: Google Identity Services (pulsante ufficiale, token d'identità con il nonce).
// Apple: Sign in with Apple JS in finestra (popup), token d'identità con il nonce.
// Gli script si caricano solo quando si apre la schermata di accesso. Nelle app Android e iOS l'accesso passa dai
// componenti nativi (arrivano con le build native): qui `disponibileSuQuestaPiattaforma` dice se il web basta.
import { PIATTAFORMA } from "../costanti.ts";

const SCRIPT_GOOGLE = "https://accounts.google.com/gsi/client";
// Lingua dello script Apple: solo i testi della sua finestra; il pulsante lo disegniamo noi.
const SCRIPT_APPLE = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

interface GoogleId {
  initialize(o: {
    client_id: string;
    nonce: string;
    callback: (r: { credential?: string }) => void;
    use_fedcm_for_prompt?: boolean;
    itp_support?: boolean;
  }): void;
  renderButton(el: HTMLElement, o: Record<string, unknown>): void;
  cancel(): void;
}
interface AppleId {
  auth: {
    init(o: { clientId: string; scope: string; redirectURI: string; usePopup: boolean; nonce: string; state?: string }): void;
    signIn(): Promise<{ authorization?: { id_token?: string } }>;
  };
}
declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleId } };
    AppleID?: AppleId;
  }
}

const caricati = new Map<string, Promise<void>>();
function caricaScript(src: string): Promise<void> {
  if (!caricati.has(src)) {
    const p = new Promise<void>((ok, no) => {
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = () => ok();
      s.onerror = () => {
        caricati.delete(src);
        no(new Error(`script non caricato: ${src}`));
      };
      document.head.appendChild(s);
    });
    caricati.set(src, p);
  }
  return caricati.get(src)!;
}

/** Sul web l'accesso funziona qui; nelle app native serve il componente nativo (non ancora incluso). */
export const disponibileSuQuestaPiattaforma = (): boolean => PIATTAFORMA === "web";

/**
 * Disegna il pulsante ufficiale di Google in `el`. Al tocco, Google dà il token d'identità e si chiama `suToken`.
 * `lingua`: la lingua dell'app per il testo del pulsante.
 */
export async function pulsanteGoogle(
  el: HTMLElement,
  clientId: string,
  nonce: string,
  lingua: string,
  suToken: (token: string) => void,
): Promise<void> {
  await caricaScript(SCRIPT_GOOGLE);
  const id = window.google?.accounts?.id;
  if (!id) throw new Error("Google Identity Services non disponibile");
  id.initialize({
    client_id: clientId,
    nonce,
    callback: (r) => r.credential && suToken(r.credential),
    use_fedcm_for_prompt: true,
    itp_support: true,
  });
  id.renderButton(el, { type: "standard", theme: "outline", size: "large", shape: "pill", text: "continue_with", logo_alignment: "center", locale: lingua, width: 276 });
}

/** Apre la finestra di Apple; restituisce il token d'identità (null se chi usa l'app chiude la finestra). */
export async function accediConApple(serviceId: string, nonce: string): Promise<string | null> {
  await caricaScript(SCRIPT_APPLE);
  const a = window.AppleID;
  if (!a) throw new Error("Sign in with Apple non disponibile");
  // redirectURI: deve essere registrato nel Service ID di Apple (è l'indirizzo del sito, vedi GUIDA-STORE-E-SERVIZI).
  a.auth.init({ clientId: serviceId, scope: "name email", redirectURI: `${window.location.origin}/`, usePopup: true, nonce });
  try {
    const r = await a.auth.signIn();
    return r.authorization?.id_token ?? null;
  } catch (e) {
    // Finestra chiusa da chi usa l'app: non è un errore da mostrare.
    if ((e as { error?: string })?.error === "popup_closed_by_user") return null;
    throw e;
  }
}
